import Vue from 'vue';
import { getContentArea } from '@/contentful/index';
import {
    dateFormat,
    dateFormatYYYYMMDD,
    PdfMake,
  } from '@/constants';
import { isNil, mapKeys } from 'lodash';
import {
    MemberAccountResponseModel,
    PdfMakeTableCell,
    PdfMakeTableCellStyles,
    TableHeader,
    ClaimsPaymentResponseModel,
  } from '@/@types/member';
import moment from 'moment';
import markdownIt from 'markdown-it/dist/markdown-it.min.js';
import htmlToPdfmake from 'html-to-pdfmake';
import GeneratePDFService from '@/services/GeneratePDF.service';

interface Data {
    APIDateFormat: string;
    pdfData: PdfMakeTableCell[][];
    pdfLogo: string;
    reportStartDate: string;
    reportEndDate: string;
    claimsData: PdfMakeTableCell[][];
    content: string;
    pdfTagline: string;
}
export default Vue.extend({

    data(): Data {
        return {
          APIDateFormat: dateFormatYYYYMMDD,
          pdfData: [],
          pdfLogo: '',
          reportStartDate: '',
          reportEndDate: '',
          claimsData: [],
          content: '',
          pdfTagline: '',
        };
      },

    beforeMount() {
        // Generate base64 logo for PDF generation
        const CANVAS = document.createElement('CANVAS') as HTMLCanvasElement;
        const CTX: CanvasRenderingContext2D = CANVAS.getContext('2d')!;
        const IMG = document.createElement('img');
        try {
          // use pdf-specific logo if present
          IMG.src = require('@/horizon-branding-module/static/logo_pdf.svg');
        } catch (error) {
          // fall back to default logo used elsewhere
          IMG.src = require('@/horizon-branding-module/static/logo_dark.svg');
        }
        IMG.onload = () => {
            CANVAS.width = IMG.width;
            CANVAS.height = IMG.height;
            CTX.drawImage(IMG, 0, 0);
            this.pdfLogo = CANVAS.toDataURL();
        };
        this.$store.dispatch('getPersonalInformation');
        this.$store.dispatch('getMemberCustomerServiceInformation');
        this.getContent();
        this.getTagline();
    },

    computed: {
        account(): MemberAccountResponseModel {
          return this.$store.getters.currentAccount(this.$route.params.id);
        },
        accountKey(): string {
          return this.$route.params.id;
        },
        claimsHeaders(): TableHeader[] {
        return [
            {
            text: this.$options.filters!.t('pdf-eop-account'),
            value: 'account',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-source'),
            value: 'source',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-claim-number'),
            value: 'claimNumber',
            align: 'right',
            },
            {
            text: this.$options.filters!.t('pdf-eop-service-type'),
            value: 'serviceType',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-patient'),
            value: 'patientName',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-service-from-date'),
            value: 'serviceFromDate',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-service-to-date'),
            value: 'serviceToDate',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-eop-amount-submitted'),
            value: 'submittedAmount',
            align: 'right',
            },
            {
            text: this.$options.filters!.t('pdf-eop-amount-approved'),
            value: 'approvedAmount',
            align: 'right',
            },
            {
            text: this.$options.filters!.t('pdf-eop-denial-adjustment-reason'),
            value: 'denialReason',
            align: 'left',
            },
        ];
      },
    },

    methods: {
      generatePdfTableHeaders(headers: TableHeader[], customStyles: PdfMakeTableCellStyles = {}): PdfMakeTableCell[] {
        return headers.map((item: any): PdfMakeTableCell => {
          return {
            ...PdfMake.TABLE_HEADER,
            text: item.text, // item.text.replace(/\s/g, '\n'),
            ...customStyles,
          };
        });
      },
      generatePdfTableCells(items: any[], customStyles: PdfMakeTableCellStyles = {}): PdfMakeTableCell[][] {
        return items.reduce((cells: any[], item: any) => {
          cells.push(Object.values(item).map((cellValue): PdfMakeTableCell => {
            const TEXT = !isNil(cellValue) ? cellValue.toString() : '';
            const IS_AMOUNT_FIELD = TEXT && (TEXT.startsWith('$') || TEXT.startsWith('+') || TEXT.startsWith('-')) ;
            return {
              ...PdfMake.TABLE_CELL,
              text: TEXT,
              ...(IS_AMOUNT_FIELD ? {
                alignment: 'right',
              } : {}),
              ...customStyles,
            };
          }));
          return cells;
        }, []);
      },
      getClaimsDataForPdf(paymentDetails) {
        const TRANSACTIONS = paymentDetails.claims.map((claim: ClaimsPaymentResponseModel) => {
          return {
            account: claim.account,
            source: claim.source,
            claimNumber: claim.claimNumber,
            serviceType: claim.serviceType,
            patientName: claim.patientName,
            serviceStartDate: moment(claim.serviceStartDate).format(dateFormat),
            serviceEndDate: moment(claim.serviceEndDate).format(dateFormat),
            submittedAmount: this.$options.filters.transactionFormat(this.$options.filters.currency(claim.submittedAmount)),
            approvedAmount: this.$options.filters.transactionFormat(this.$options.filters.currency(claim.approvedAmount)),
            denialReason: claim.denialReason === '<Not Applicable>' ? this.$options.filters.t('pdf-eop-not-applicable') : claim.denialReason,
          };
        });
        const headerCellStyles = {
          fontSize: 7,
          fillColor: '#fff',
          bold: true,
          nowrap: true,
          borderColor: ['#fff', '#fff', '#fff', '#fff'],
        };
        const cellStyles = {
          fontSize: 7,
        };
        const TRANSACTIONS_CELLS = this.generatePdfTableCells(TRANSACTIONS, cellStyles);
        TRANSACTIONS_CELLS.unshift(this.generatePdfTableHeaders(this.claimsHeaders, headerCellStyles));
        this.pdfData = TRANSACTIONS_CELLS;
      },
      createPdf(transactionId: number, paymentDetails: ClaimsPaymentResponseModel) {
        this.getClaimsDataForPdf(paymentDetails);
        const user = this.$store.getters.user;
        GeneratePDFService
          .createPdf({
            pageOrientation: 'portrait',
            header: (currentPage, pageCount) => {
              return [
                {
                  columns: [
                    {
                      stack: [
                        {
                          image: this.pdfLogo,
                          width: 150,
                          },
                          (currentPage === 1 && this.pdfTagline.length) ?
                            {
                            text: this.pdfTagline,
                            margin: [0, 5, 0, 0],
                            fontSize: 7,
                            } : null,
                        ],
                      margin: [50, 40, 0, 0],
                    },
                    {
                      alignment: 'right',
                      margin: [0, 40, 40, 0],
                      text: moment().format(dateFormat),
                    },
                  ],
                },
              ];
            },
            content:
            [
              {
                text: this.$options.filters!.t('pdf-eop-explanation-of-payment'),
                style: 'header',
              },
              {
                columns: [
                  {
                    text: this.$options.filters!.t('pdf-general-participant'),
                    style: 'infoText',
                    width: 100,
                  },
                  {
                    text: `${user.firstName} ${user.lastName} (${user.selectAccountId})`,
                    style: 'infoText',
                  },
                ],
              },
              {
                columns: [
                  [
                    {
                      text: this.$options.filters!.t('pdf-eop-summary'),
                      style: 'header2',
                    },
                    {
                      columns: [
                        {
                          text: this.$options.filters!.t('pdf-eop-total-paid'),
                          width: 100,
                          alignment: 'left',
                        },
                        {
                          text: this.$options.filters!.currency(paymentDetails.paymentAmount),
                          width: 100,
                          alignment: 'right',
                        },
                      ],
                    },
                    {
                      columns: [
                        {
                          text: this.$options.filters!.t('pdf-eop-payment-type'),
                          width: 100,
                          alignment: 'left',
                        },
                        {
                          text: paymentDetails.paymentType,
                          width: 100,
                          alignment: 'right',
                        },
                      ],
                    },
                    {
                      columns: [
                        {
                          text: this.$options.filters!.t('pdf-eop-payment-issue-date'),
                          width: 100,
                          alignment: 'left',
                        },
                        {
                          text: moment(paymentDetails.paymentDate).format(dateFormat),
                          width: 100,
                          alignment: 'right',
                        },
                      ],
                    },
                    {
                      columns: [
                        {
                          text: this.$options.filters!.t('pdf-eop-payee'),
                          width: 100,
                          alignment: 'left',
                        },
                        {
                          text: paymentDetails.payee,
                          width: 100,
                          alignment: 'right',
                        },
                      ],
                    },
                  ],
                  {
                    text: '',
                    width: 20,
                  },
                  [
                    {
                      text: this.$options.filters!.t('pdf-eop-appeal-rights'),
                      style: 'header2',
                    },
                    {
                      text: this.$options.filters!.t('pdf-eop-appeal-rights-description'),
                    },
                  ],
                ],
              },
              {
                text: this.$options.filters!.t('pdf-eop-claims'),
                style: 'header2',
                margin: [0, 30, 0, 0],
              },
              {
                columns: [
                  {
                    style: 'defaultTable',
                    table: {
                      //   headerRows: 1,
                      style: 'defaultTable',
                      dontBreakRows: true,
                      body: this.pdfData,
                      widths: ['auto', 'auto', 'auto', 'auto', 'auto', 44, 'auto', 'auto', 'auto', 'auto'],
                      alignments: ['left', 'left', 'left', 'left', 'left', 'left', 'left', 'right', 'right', 'left'],
                    },
                    margin: [0, 10, 0, 10],
                    layout: PdfMake.TABLE_LAYOUT,
                  },
                ],
              },
              [
                {
                  text: '',
                  pageBreak: 'before',
                },
                ...htmlToPdfmake(
                  this.content,
                  {
                    defaultStyles: {
                      h1: {
                        fontSize: 15,
                      },
                    },
                  },
                ),
              ],
            ],
            footer: null,
            styles: {
              defaultTable: {
                margin: [0, 0, 0, 0],
                alignment: 'left',
                fontSize: 7,
              },
              header: {
                bold: true,
                fontSize: 15,
                margin: [0, 20, 0, 5],
              },
              header2: {
                bold: true,
                fontSize: 12,
                margin: [0, 10, 0, 5],
                decoration: 'underline',
              },
              infoText: {
                margin: [0, 3, 0, 3],
              },
              'html-h1': {
                bold: true,
                fontSize: 5,
                margin: [0, 20, 0, 5],
              },
            },
            defaultStyle: {
              fontSize: 8.5,
              color: '#222',
              lineHeight: 1.3,
            },
            pageMargins: [ 50, 100, 50, 40 ],
            pageSize: 'LETTER',
          },
          `explanation-of-payment-${transactionId}`);
      },
      getContent(): void {
        getContentArea('important-claim-information')
          .then((data) => {
            if (data) {
              const md = new markdownIt();
              this.content = md.render(data.fields.helpContent);
            }
          },
        );
      },
      getTagline(): void {
          getContentArea('pdf-tagline')
              .then((data) => {
              if (data) {
                  this.pdfTagline = data.fields.content;
              }
          });
      },
    },
});

